import { Pagination, Table } from 'antd';
import { Open16Regular } from '@fluentui/react-icons';
import { ColumnsType } from 'antd/lib/table/interface';
import * as React from 'react';
import { useQuery } from 'react-query';
import { IMemberStatementDistributionPool } from '../../../types/IMemberStatementSearchResult';
import IMembersPortalComponentDataItem from '../../../../redux/types/IMembersPortalComponentDataItem';
import { mp_addTab } from '../../../../redux/reducers/MembersPortalReducer';
import { ITabReduxItem } from '../../../../redux/types/ITabReduxItem';
import { MEMBER_STATEMENTS_WORKS_PAGE } from '../../../Consts';
import IconTextButton from '../../../../repertoire/components/iconTextButton/IconTextButton';
import { MembersPortalService } from '../../../../redux/services/MembersPortalService';
import ErrorFallback from '../../../../telemetry/components/ErrorFallback';
import { formatCurrencyWithCommas } from '../../../../util/tools';

export interface IMemberStatementDetailsGridProps {
    paymentDetailGridsComponentData: IMembersPortalComponentDataItem;
    addTab: typeof mp_addTab;
    paymentRunCode: string;
    paymentRunID: number;
    description: string;
    userNameNumber: string;
    refreshKey?: number;
}

export interface IMemberStatementDetailsGridRowType {
    key: number | string;
    distributionCode: string;
    poolCode: string;
    segment: string;
    detailAmount: string;
}

const PAGE_SIZE = 20;

export const MemberStatementDetailsGrid: React.FC<IMemberStatementDetailsGridProps> = (props) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE);

    const {
        data,
        isLoading,
        isError,
        error
    } = useQuery<IMemberStatementDistributionPool[]>({
        queryKey: [
            'statement-distribution-pools',
            props.refreshKey
        ],
        queryFn: () =>
            MembersPortalService.getStatementDistributionPools(
                props.paymentRunID,
                props.userNameNumber
            ),
        refetchOnWindowFocus: false,
        staleTime: Infinity
    });

    const openWorks = (record: IMemberStatementDetailsGridRowType) => {
        const { addTab, paymentRunCode, description } = props;

        const usageDetailsTab: ITabReduxItem = {
            tabTitle: `${record.distributionCode} ${record.poolCode} ${record.segment}`,
            title: `${record.distributionCode} ${record.poolCode} ${record.segment}`,
            component: MEMBER_STATEMENTS_WORKS_PAGE.component,
            memberStatementWorkState: {
                distributionCode: record.distributionCode,
                poolCode: record.poolCode,
                segment: record.segment,
                description,
                paymentRunCode,
                paymentRunID: props.paymentRunID,
                refreshKey: Date.now()
            }
        };

        addTab(usageDetailsTab);
    };

    const dataSource: IMemberStatementDetailsGridRowType[] =
        (data ?? []).map(prd => ({
            distributionCode: prd.distributionCode,
            poolCode: prd.poolCode,
            segment: prd.segment,
            detailAmount: formatCurrencyWithCommas(prd.amount),
            key: `${prd.distributionCode}-${prd.poolCode}-${prd.segment}`
        }));

    const columns: ColumnsType<IMemberStatementDetailsGridRowType> =
        props.paymentDetailGridsComponentData.fields.map(f => {
            if (f.name.includes('open')) {
                return {
                    title: f.data.split('/').pop(),
                    dataIndex: f.name,
                    key: f.name,
                    fixed: 'right',
                    render: (_, record) => (
                        <IconTextButton
                            text={f.data.split('/').pop()}
                            icon={Open16Regular}
                            onClick={() => openWorks(record)}
                        />
                    )
                };
            }

            return {
                title: f.data,
                dataIndex: f.name,
                key: f.name,
                sorter: ['distributionCode', 'poolCode'].includes(f.name)
                    ? (a, b) => a[f.name].localeCompare(b[f.name])
                    : undefined
            };
        });

    React.useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(dataSource.length / pageSize));
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [dataSource.length, pageSize, currentPage]);

    const pagedData = React.useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return dataSource.slice(start, start + pageSize);
    }, [dataSource, currentPage, pageSize]);

    if (isError) {
        return <ErrorFallback message={(error as Error)?.message} />;
    }

    return (
        <div>
            <div style={{ width: '100%', overflow: 'auto' }}>
                <Table
                    dataSource={pagedData}
                    columns={columns}
                    loading={isLoading}
                    pagination={false}
                />
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={dataSource.length}
                    showSizeChanger
                    onChange={(page, nextPageSize) => {
                        if (nextPageSize && nextPageSize !== pageSize) {
                            setPageSize(nextPageSize);
                            setCurrentPage(1);
                            return;
                        }
                        setCurrentPage(page);
                    }}
                />
            </div>
        </div>
    );
};
